<script setup>
import { useQuasar } from 'quasar';
import { computed, ref } from 'vue';
import { programStatuses as programStatusesConfig } from 'boot/globals';
import { i18n } from 'boot/i18n';
import { Timestamp } from 'firebase/firestore';
import { notifySuccess } from 'src/composables/interactions';
import { notifyErrorWithDetails } from 'src/utils/AppUtils';
import { PROGRAM_DATA_TYPES, PROGRAM_TYPES } from 'src/constants/programs';
import ProgramsDB from 'src/fb/programs-db';
import { HrDropdownMenu, HrTooltip, HrList, HrListItem, HrIcon } from 'src/design-system';

const props = defineProps({
  selectedPrograms: {
    type: Array,
    required: true,
  },

  addStatusActivity: {
    type: Function,
    required: true,
  },
});

// Emitted after a successful status change so the parent can clear its own
// selection state (which differs between pages: selectedPrograms vs ids).
const emit = defineEmits(['updated']);

const $q = useQuasar();
const programsDb = new ProgramsDB();

const isProcessing = ref(false);

const programStatuses = computed(() => Object.values(programStatusesConfig).map((programStatus) => ({
  ...programStatus,
  label: i18n.t(programStatus.label),
})));

const isOnlyABCProgramSelected = computed(() => {
  const selectedProgram = props.selectedPrograms[0];
  if (props.selectedPrograms.length === 1 && selectedProgram) {
    const { type, dataType } = selectedProgram;
    return type === PROGRAM_TYPES.BEHAVIOUR && dataType === PROGRAM_DATA_TYPES.INCIDENTS;
  }
  return false;
});

// A library program belongs to a clinic, not a client, so a client-scoped activity for it is both
// meaningless and rejected by the activities security rule. EditProgram.vue guards the same way.
const isProgramFromLibrary = (program) => !program.client && !!program.clinic;

const onBulkSetStatus = (status) => {
  if (isProcessing.value) {
    return;
  }

  const statusLabel = programStatuses.value.find((programStatus) => programStatus.value === status)?.label;
  $q.dialog({
    title: i18n.t('common.dialogTitleConfirm'),
    message: i18n.t('programs.dialogBulkSetProgramsStatusMessage', {
      count: props.selectedPrograms.length,
      status: statusLabel,
    }),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    try {
      isProcessing.value = true;
      const programStatusActions = props.selectedPrograms.map(async (program) => {
        if (!program || program.dataType === PROGRAM_DATA_TYPES.INCIDENTS) {
          return;
        }
        if (program.status !== status) {
          await programsDb.update({
            id: program.id,
            status,
            [`statusChanges.${status}`]: Timestamp.now(),
          });
          if (!isProgramFromLibrary(program)) {
            await props.addStatusActivity(program, program.status, status);
          }
        }
      });

      await Promise.all(programStatusActions);
      emit('updated');
      notifySuccess(i18n.t('programs.notifyBulkSetProgramsStatusSuccess'));
    } catch (err) {
      notifyErrorWithDetails(i18n.t('programs.notifyBulkSetProgramsStatusFailed'), err?.message);
    } finally {
      isProcessing.value = false;
    }
  });
}; // onBulkSetStatus()
</script>

<template>
  <div>
    <hr-dropdown-menu
      :label="$t('programs.buttonBulkSetProgramStatus')"
      color="secondary"
      size="sm"
      :disable="isOnlyABCProgramSelected || isProcessing"
    >
      <hr-list>
        <hr-list-item
          v-for="programStatus in programStatuses"
          :key="programStatus.value"
          v-close-popup
          clickable
          :label="programStatus.label"
          @click="onBulkSetStatus(programStatus.value)"
        >
          <template #leading>
            <hr-icon
              size="sm"
              :name="programStatus.icon"
            />
          </template>
        </hr-list-item>
      </hr-list>
    </hr-dropdown-menu>

    <hr-tooltip v-if="isOnlyABCProgramSelected">
      {{ $t('programs.abcStatusChangeTooltip') }}
    </hr-tooltip>
  </div>
</template>
